import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Callout,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldLabel,
  ReasonField,
  Select,
  TextField,
  errorText,
  useCsrf,
  useReason,
  useToast,
  type Reason,
} from "@amber/ui";
import {
  ADDABLE_RELATIONSHIPS,
  RELATIONSHIP_LABELS,
  copyChangeResponseSchema,
  type AddCopyPreview,
  type CommunicationView,
  type CopyCandidate,
  type EvidenceRecord,
} from "../../contracts/index";
import { EVIDENCE_ERROR_FALLBACK, REQUIRED_REASON, clock } from "../format";
import { addCopyPreviewQuery, copyCandidatesQuery, useEvidenceCommand } from "../queries";
import { rangeLabel } from "./Copies";

export function parseClock(text: string): number | null {
  const parts = text.trim().split(":");
  if (parts.length === 0 || parts.length > 3 || parts.some((p) => p === "" || Number.isNaN(Number(p)))) return null;
  const seconds = parts.reduce((total, p) => total * 60 + Number(p), 0);
  return Number.isFinite(seconds) && seconds >= 0 ? Math.round(seconds * 1000) : null;
}

function RangeFields({
  legend,
  value,
  onChange,
  error,
}: {
  legend: string;
  value: [string, string];
  onChange: (v: [string, string]) => void;
  error?: string | null;
}) {
  return (
    <fieldset className="m-0 mt-3 min-w-0 border-0 p-0">
      <legend className="mb-1 text-sm font-strong tracking-[0.04em] text-muted uppercase">{legend}</legend>
      <div className="flex items-center gap-2">
        <input
          aria-label={`${legend}: start`}
          className="h-8 w-24 rounded-md border border-border bg-bg px-2 font-mono text-ui"
          value={value[0]}
          onChange={(e) => {
            onChange([e.target.value, value[1]]);
          }}
        />
        <span aria-hidden="true">–</span>
        <input
          aria-label={`${legend}: end`}
          className="h-8 w-24 rounded-md border border-border bg-bg px-2 font-mono text-ui"
          value={value[1]}
          onChange={(e) => {
            onChange([value[0], e.target.value]);
          }}
        />
        <span className="text-md text-muted">mm:ss</span>
      </div>
      {error ? <p className="m-0 mt-1 text-md text-attention">{error}</p> : null}
    </fieldset>
  );
}

type AddableRelationship = (typeof ADDABLE_RELATIONSHIPS)[number];
type MsRange = readonly [number | null, number | null];

const RELATIONSHIP_OPTIONS: Record<AddableRelationship, string> = {
  full_original: "The complete original publication",
  edited_edition: "A longer edit that keeps most of the communication",
  extract: "A clip or excerpt of part of it",
  preview: "A preview or trailer; never complete",
  dub: "Dubbed into another language; never primary",
  translation: "A translation; never primary",
};

function rangeError(r: MsRange): string | null {
  if (r[0] === null || r[1] === null) return "Enter times as mm:ss";
  return r[0] >= r[1] ? "The range must start before it ends" : null;
}

interface StepOneErrors {
  readonly item: string | null;
  readonly com: string | null;
  readonly here: string | null;
  readonly there: string | null;
  readonly evidence: string | null;
}

function stepOneErrors(input: {
  otherItem: string;
  otherCom: string;
  candidate: CopyCandidate | undefined;
  hereMs: MsRange;
  thereMs: MsRange;
  evidence: string;
}): StepOneErrors {
  const { otherItem, otherCom, candidate } = input;
  return {
    item: otherItem ? null : "Choose the other item",
    com:
      candidate && candidate.communications.length > 1 && !otherCom
        ? "Choose the communication in the other item"
        : null,
    here: rangeError(input.hereMs),
    there: rangeError(input.thereMs),
    evidence: input.evidence.trim() ? null : "Name the matching position or add a note",
  };
}

function CommunicationField({
  record,
  value,
  onChange,
}: {
  record: EvidenceRecord;
  value: string;
  onChange: (value: string) => void;
}) {
  if (record.communications.length <= 1) return null;
  return (
    <div className="mt-3">
      <FieldLabel htmlFor="copy-com" required>
        Communication
      </FieldLabel>
      <Select
        id="copy-com"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        {record.communications.map((c) => (
          <option key={c.id} value={c.id}>
            {c.title ?? `Communication ${String(c.ordinal)}`} · {rangeLabel(c.range_ms)}
          </option>
        ))}
      </Select>
    </div>
  );
}

function OtherItemField({
  value,
  onChange,
  loading,
  candidates,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  loading: boolean;
  candidates: readonly CopyCandidate[];
  error: string | null;
}) {
  return (
    <div className="mt-3">
      <FieldLabel htmlFor="copy-item" required>
        Other item
      </FieldLabel>
      <Select
        id="copy-item"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        aria-invalid={error ? true : undefined}
      >
        <option value="">{loading ? "Loading candidates…" : "Choose an item"}</option>
        {candidates.map((c) => (
          <option key={c.item_id} value={c.item_id}>
            {c.title}
            {c.shared_people.length > 0 ? ` · shares ${c.shared_people.join(", ")}` : ""}
            {c.duration_ms ? ` · ${clock(c.duration_ms)}` : ""}
          </option>
        ))}
      </Select>
      <p className="m-0 mt-1 text-md text-muted">
        Suggested candidates share people, programme or date with this item.
      </p>
      {error ? <p className="m-0 mt-1 text-md text-attention">{error}</p> : null}
    </div>
  );
}

function OtherCommunicationField({
  candidate,
  value,
  onChange,
}: {
  candidate: CopyCandidate | undefined;
  value: string;
  onChange: (value: string) => void;
}) {
  if (!candidate || candidate.communications.length <= 1) return null;
  return (
    <div className="mt-3">
      <FieldLabel htmlFor="copy-other-com" required>
        Communication in the other item
      </FieldLabel>
      <Select
        id="copy-other-com"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        <option value="">Choose</option>
        {candidate.communications.map((c) => (
          <option key={c.id} value={c.id}>
            {c.title ?? `Communication ${String(c.ordinal)}`} · {rangeLabel(c.range_ms)}
          </option>
        ))}
      </Select>
    </div>
  );
}

function RelationshipField({
  value,
  onChange,
}: {
  value: AddableRelationship;
  onChange: (value: AddableRelationship) => void;
}) {
  return (
    <div className="mt-3">
      <FieldLabel htmlFor="copy-rel" required>
        Relationship
      </FieldLabel>
      <Select
        id="copy-rel"
        value={value}
        onChange={(e) => {
          onChange(e.target.value as AddableRelationship);
        }}
      >
        {ADDABLE_RELATIONSHIPS.map((r) => (
          <option key={r} value={r}>
            {r === "extract" ? "Clip or excerpt" : RELATIONSHIP_LABELS[r]} · {RELATIONSHIP_OPTIONS[r]}
          </option>
        ))}
      </Select>
    </div>
  );
}

interface StepOneProps {
  readonly record: EvidenceRecord;
  readonly comId: string;
  readonly onCommunication: (value: string) => void;
  readonly candidatesLoading: boolean;
  readonly candidates: readonly CopyCandidate[];
  readonly candidate: CopyCandidate | undefined;
  readonly otherItem: string;
  readonly setOtherItem: (value: string) => void;
  readonly otherCom: string;
  readonly onOtherCommunication: (value: string) => void;
  readonly here: [string, string];
  readonly setHere: (value: [string, string]) => void;
  readonly there: [string, string];
  readonly setThere: (value: [string, string]) => void;
  readonly relationship: AddableRelationship;
  readonly setRelationship: (value: AddableRelationship) => void;
  readonly evidence: string;
  readonly setEvidence: (value: string) => void;
  readonly errors: StepOneErrors;
  readonly submitted: boolean;
}

function AddCopyStepOne(props: StepOneProps) {
  const { errors, submitted } = props;
  return (
    <>
      <DialogText>
        Copies are linked per communication. Choose the communication here, the other item, and the time range of the
        communication in each.
      </DialogText>
      <CommunicationField record={props.record} value={props.comId} onChange={props.onCommunication} />
      <OtherItemField
        value={props.otherItem}
        onChange={props.setOtherItem}
        loading={props.candidatesLoading}
        candidates={props.candidates}
        error={submitted ? errors.item : null}
      />
      <OtherCommunicationField
        candidate={props.candidate}
        value={props.otherCom}
        onChange={props.onOtherCommunication}
      />
      <div className="grid grid-cols-2 gap-x-4 max-[640px]:grid-cols-1">
        <RangeFields
          legend="Range in this item"
          value={props.here}
          onChange={props.setHere}
          error={submitted ? errors.here : null}
        />
        <RangeFields
          legend="Range in the other item"
          value={props.there}
          onChange={props.setThere}
          error={submitted ? errors.there : null}
        />
      </div>
      <RelationshipField value={props.relationship} onChange={props.setRelationship} />
      <TextField
        label="Evidence"
        required
        value={props.evidence}
        onChange={(e) => {
          props.setEvidence(e.target.value);
        }}
        placeholder="Matching position (e.g. opening line at 0:05) or a note"
        error={submitted ? errors.evidence : null}
      />
    </>
  );
}

function CopyGrouping({ preview }: { preview: AddCopyPreview }) {
  return (
    <>
      <DialogText>
        <b>{preview.communication.title}</b> will be grouped as:
      </DialogText>
      <ul className="m-0 list-none p-0 text-md">
        {preview.grouping.map((g) => (
          <li key={g.item_id} className="flex items-baseline gap-2 border-b border-divider py-1.5">
            <span className="min-w-0 flex-1">
              <span className="font-strong">{g.title}</span>{" "}
              <span className="text-muted">
                · {g.relationship} · {rangeLabel(g.range_ms)}
              </span>
            </span>
            {g.role === "primary" ? (
              <span className="rounded-full border border-border px-1.5 text-sm font-strong">Primary</span>
            ) : null}
            {g.is_new ? <span className="text-sm text-muted">new</span> : null}
          </li>
        ))}
      </ul>
      <ul className="m-0 mt-2 list-disc pl-5 text-md text-muted">
        <li>Primary copy afterwards: {preview.primary_after}</li>
        <li>
          Alignment: {preview.alignment.confident} passage
          {preview.alignment.confident === 1 ? "" : "s"} aligned
          {preview.alignment.uncertain > 0 ? `, ${String(preview.alignment.uncertain)} uncertain` : ""}
        </li>
        {preview.unaffected.map((u) => (
          <li key={u}>{u}</li>
        ))}
      </ul>
    </>
  );
}

function AddCopyStepTwo({
  loading,
  previewError,
  preview,
  reason,
  saveError,
}: {
  loading: boolean;
  previewError: unknown;
  preview: AddCopyPreview | undefined;
  reason: Reason;
  saveError: unknown;
}) {
  return (
    <>
      {loading ? <DialogText>Preparing the preview…</DialogText> : null}
      {previewError ? (
        <Callout tone="attention" role="alert">
          {errorText(previewError, EVIDENCE_ERROR_FALLBACK)}
        </Callout>
      ) : null}
      {preview ? <CopyGrouping preview={preview} /> : null}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={saveError} fallback={EVIDENCE_ERROR_FALLBACK} />
    </>
  );
}

export function AddCopyDialog({
  record,
  communication,
  onClose,
}: {
  record: EvidenceRecord;
  communication: CommunicationView;
  onClose: () => void;
}) {
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [comId, setComId] = useState(communication.id);
  const com = record.communications.find((c) => c.id === comId) ?? communication;
  const ownRange = com.range_ms ?? [0, record.item.duration_ms ?? record.turns.at(-1)?.end_ms ?? 0];
  const [otherItem, setOtherItem] = useState("");
  const [otherCom, setOtherCom] = useState("");
  const [here, setHere] = useState<[string, string]>([clock(ownRange[0]), clock(ownRange[1])]);
  const [there, setThere] = useState<[string, string]>(["00:00", ""]);
  const [relationship, setRelationship] = useState<AddableRelationship>("extract");
  const [evidence, setEvidence] = useState("");
  const reason = useReason(null, REQUIRED_REASON);
  const [submitted, setSubmitted] = useState(false);
  const candidates = useQuery(copyCandidatesQuery(record.item.id));
  const candidate = candidates.data?.candidates.find((c) => c.item_id === otherItem);
  useEffect(() => {
    if (!candidate) return;
    const only = candidate.communications.length === 1 ? candidate.communications[0] : undefined;
    setOtherCom(only?.id ?? "");
    const end = only?.range_ms?.[1] ?? candidate.duration_ms;
    setThere([clock(only?.range_ms?.[0] ?? 0), end ? clock(end) : ""]);
  }, [candidate]);
  const hereMs = [parseClock(here[0]), parseClock(here[1])] as const;
  const thereMs = [parseClock(there[0]), parseClock(there[1])] as const;
  const body = {
    communication_id: com.id,
    other_item_id: otherItem,
    ...(otherCom ? { other_communication_id: otherCom } : {}),
    range_here_ms: [hereMs[0] ?? 0, hereMs[1] ?? 1],
    range_there_ms: [thereMs[0] ?? 0, thereMs[1] ?? 1],
    relationship,
    evidence: evidence.trim() || "-",
    reason: reason.trimmed || "-",
  };
  const csrf = useCsrf();
  const preview = useQuery(
    addCopyPreviewQuery(
      record.item.id,
      [body.communication_id, otherItem, otherCom, here, there, relationship],
      body,
      step === 2,
      csrf,
    ),
  );
  const save = useEvidenceCommand(record.item.id, copyChangeResponseSchema);
  const step1Errors = stepOneErrors({ otherItem, otherCom, candidate, hereMs, thereMs, evidence });
  const valid = Object.values(step1Errors).every((e) => e === null);
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={step === 1 ? "Add a copy…" : "Add this copy?"}
      onSubmit={() => {
        if (step === 1) {
          setSubmitted(true);
          if (valid) {
            setSubmitted(false);
            setStep(2);
          }
          return;
        }
        if (reason.submit())
          save.mutate(
            {
              path: `/api/evidence/items/${record.item.id}/copies`,
              body: { ...body, reason: reason.trimmed, evidence: evidence.trim() },
            },
            {
              onSuccess: (out) => {
                toast.show(out.message);
                onClose();
              },
            },
          );
      }}
      footer={
        step === 1 ? (
          <DialogFooter onClose={onClose} confirm="Preview" />
        ) : (
          <DialogFooter
            onClose={() => {
              setStep(1);
            }}
            cancel="Back"
            confirm="Add copy"
            disabled={!preview.data}
            pending={save.isPending}
            failed={save.isError}
          />
        )
      }
    >
      {step === 1 ? (
        <AddCopyStepOne
          record={record}
          comId={comId}
          onCommunication={(value) => {
            setComId(value as typeof comId);
            const c = record.communications.find((x) => x.id === value);
            const r = c?.range_ms ?? ownRange;
            setHere([clock(r[0]), clock(r[1])]);
          }}
          candidatesLoading={candidates.isLoading}
          candidates={candidates.data?.candidates ?? []}
          candidate={candidate}
          otherItem={otherItem}
          setOtherItem={setOtherItem}
          otherCom={otherCom}
          onOtherCommunication={(value) => {
            setOtherCom(value);
            const c = candidate?.communications.find((x) => x.id === value);
            if (c?.range_ms) setThere([clock(c.range_ms[0]), clock(c.range_ms[1])]);
          }}
          here={here}
          setHere={setHere}
          there={there}
          setThere={setThere}
          relationship={relationship}
          setRelationship={setRelationship}
          evidence={evidence}
          setEvidence={setEvidence}
          errors={step1Errors}
          submitted={submitted}
        />
      ) : (
        <AddCopyStepTwo
          loading={preview.isLoading}
          previewError={preview.error}
          preview={preview.data}
          reason={reason}
          saveError={save.error}
        />
      )}
    </Dialog>
  );
}
